/**
 * InRoomDisplayTab — the "In-room Display" tab of Guest Experience (the wedge).
 *
 * Handoff layout: a large e-ink live-preview card + a device-status card + a
 * "How it adapts" note + clickable stay-phase cards that re-render the preview.
 * The device-status connection state is wired to the real TRMNL integration
 * (apiClient.getTrmnlIntegration). The phase preview content is an illustrative
 * simulation ("preview what your guest sees"), not live guest data.
 *
 * "Manage display" reveals the full DisplayBehaviourPage — TRMNL token lifecycle,
 * TV pairing + slide editor, Night Mode, multilingual (EN/SV/DE) fallback
 * messages, and the trivia pool — all preserved.
 */

import { useEffect, useState } from "react"
import { Sparkles, Check, ArrowLeft, Tablet } from "lucide-react"
import { apiClient } from "@/lib/api"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { cn } from "@/lib/utils"
import { DisplayBehaviourPage } from "@/pages/DisplayBehaviourPage"

type PhaseKey = "before" | "arrival" | "during" | "checkout"

interface PhaseDef {
  key: PhaseKey
  phase: string
  title: string
  body: string
  eink: { kicker: string; greeting: string; body: string; rows: { label: string; value: string }[] }
}

const PHASES: PhaseDef[] = [
  {
    key: "before",
    phase: "Before arrival",
    title: "Countdown & access",
    body: "Door code, parking and arrival window appear as check-in nears.",
    eink: { kicker: "Arriving Thursday", greeting: "See you soon,\nSofia.", body: "Check-in opens at 3:00 PM. Park in bay 12; the entrance is step-free from the marina side.", rows: [{ label: "Door code", value: "4417" }, { label: "Arrival", value: "Thu · 3:00 PM" }] },
  },
  {
    key: "arrival",
    phase: "Arrival day",
    title: "Warm welcome",
    body: "A personal hello with Wi-Fi and the first evening's tip.",
    eink: { kicker: "Welcome home", greeting: "Welcome,\nSofia.", body: "The kettle's stocked and the terrace is yours. Pour a glass and watch the marina wake up.", rows: [{ label: "Wi-Fi", value: "MarinaLoft_5G" }, { label: "Password", value: "sunset22" }] },
  },
  {
    key: "during",
    phase: "During stay",
    title: "Evening & weather",
    body: "Daily tips tuned to the forecast and time of day.",
    eink: { kicker: "Tuesday evening", greeting: "Good evening,\nSofia.", body: "Night has settled over the marina. Clear skies — the terrace is perfect for the last of that wine.", rows: [{ label: "Quiet hours", value: "from 10:00 PM" }, { label: "Tomorrow", value: "26° · Sunny" }] },
  },
  {
    key: "checkout",
    phase: "Checkout",
    title: "Goodbye & review",
    body: "Checkout steps in the morning, a gentle review nudge that evening.",
    eink: { kicker: "Checkout today", greeting: "Safe travels,\nSofia.", body: "Leave the keys in the lockbox — no need to strip the beds. It's been a joy having you.", rows: [{ label: "Checkout by", value: "11:00 AM" }, { label: "Taxi", value: "Tap to book" }] },
  },
]

export function InRoomDisplayTab() {
  const { propertyId } = usePropertyContext()
  const [phase, setPhase] = useState<PhaseKey>("during")
  const [connected, setConnected] = useState<boolean | null>(null)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    apiClient
      .getTrmnlIntegration(propertyId)
      .then((res) => !cancelled && setConnected(Boolean(res.ok && res.active)))
      .catch(() => !cancelled && setConnected(false))
    return () => {
      cancelled = true
    }
  }, [propertyId])

  if (editing) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="mb-4 inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          Back to display overview
        </button>
        <DisplayBehaviourPage />
      </div>
    )
  }

  const cur = PHASES.find((p) => p.key === phase) || PHASES[2]

  return (
    <div>
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.15fr_1fr]">
        {/* E-ink preview */}
        <div className="flex flex-col items-center rounded-card border border-border bg-card p-[22px] shadow-sm">
          <div className="mb-4 flex w-full items-center justify-between">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Display preview</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-strong">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              {cur.phase}
            </span>
          </div>
          <div className="w-full max-w-[360px] rounded-2xl bg-[#15110c] p-[13px]" style={{ boxShadow: "0 26px 52px -22px rgba(0,0,0,.5)" }}>
            <div
              key={phase}
              className="flex min-h-[230px] flex-col rounded-[7px] px-[22px] py-6 text-[#191919] [animation:sf-eink_.35s_ease_both]"
              style={{ background: "linear-gradient(162deg,#EEEBE3,#DAD6CB)" }}
            >
              <div className="flex items-center justify-between">
                <div className="text-[9px] font-medium uppercase tracking-[0.16em] text-[#6b6b6b]">{cur.eink.kicker}</div>
                <svg width="15" height="9" viewBox="0 0 26 13" fill="none"><rect x="1" y="1" width="21" height="11" rx="2.5" stroke="#6b6b6b" strokeWidth="1.2" /><rect x="3" y="3" width="15" height="7" rx="1" fill="#191919" /><rect x="23" y="4" width="2" height="5" rx="1" fill="#6b6b6b" /></svg>
              </div>
              <div className="mt-4 whitespace-pre-line font-serif text-[26px] font-semibold leading-[1.04] text-[#111]">
                {cur.eink.greeting}
              </div>
              <div className="mt-[11px] max-w-[34ch] text-[12.5px] leading-[1.55] text-[#3d3d3d]">{cur.eink.body}</div>
              <div className="mt-auto flex flex-col gap-[7px] border-t border-black/[0.15] pt-4">
                {cur.eink.rows.map((r) => (
                  <div key={r.label} className="flex items-center justify-between gap-3">
                    <span className="text-[10.5px] font-medium tracking-[0.02em] text-[#6b6b6b]">{r.label}</span>
                    <span className="font-mono text-[11px] font-semibold text-[#191919]">{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-[15px] max-w-[36ch] text-center text-[12px] text-muted-foreground">
            A preview of how the 7.5″ in-room display adapts through the stay.
          </p>
        </div>

        {/* Device status + how it adapts */}
        <div className="flex flex-col gap-[18px]">
          <div className="rounded-card border border-border bg-card p-5 shadow-sm">
            <div className="mb-3.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Device</div>
            <div className="mb-4 flex items-center gap-3.5">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] bg-brand-soft">
                <Tablet className="h-[22px] w-[22px] text-brand-strong" strokeWidth={1.7} />
              </span>
              <div>
                <div className="text-[14px] font-semibold text-foreground" style={{ fontWeight: 650 }}>In-room display</div>
                <div
                  className={cn(
                    "mt-0.5 inline-flex items-center gap-1.5 text-[12px] font-medium",
                    connected ? "text-brand-strong" : "text-muted-foreground",
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", connected ? "bg-brand" : "bg-muted-foreground")} />
                  {connected === null ? "Checking…" : connected ? "Connected" : "Not paired yet"}
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center justify-between border-t border-line-soft py-[9px]">
                <span className="text-[12.5px] font-medium text-ink-2">Surfaces</span>
                <span className="text-[12px] font-semibold text-foreground">E-ink · TV</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-3.5 inline-flex w-full items-center justify-center gap-1.5 rounded-[10px] bg-forest px-4 py-2.5 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              Manage display &amp; devices
            </button>
          </div>
          <div className="rounded-card border border-border p-5 shadow-sm" style={{ background: "linear-gradient(180deg,var(--sf-brand-soft),var(--sf-card) 70%)" }}>
            <div className="mb-2 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-brand-strong">
              <Sparkles className="h-3 w-3 fill-current" strokeWidth={0} />
              How it adapts
            </div>
            <p className="text-[13px] leading-[1.55] text-ink-2">
              The display reads the booking, the clock and the local forecast — shifting from arrival details, to evening
              tips, to checkout steps. You never touch it mid-stay.
            </p>
          </div>
        </div>
      </section>

      {/* Adapts through the stay */}
      <section className="rounded-card border border-border bg-card p-[22px] shadow-sm">
        <h3 className="mb-1 font-display text-[16px] font-bold tracking-[-0.01em] text-foreground">Adapts through the stay</h3>
        <p className="mb-4 text-[13px] text-muted-foreground">Select a moment to preview what your guest sees.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PHASES.map((p) => {
            const active = p.key === phase
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setPhase(p.key)}
                className={cn(
                  "rounded-[13px] border-[1.5px] p-[15px] text-left transition-all",
                  active ? "border-brand bg-brand-soft" : "border-border bg-card hover:border-line-2",
                )}
              >
                <div className="mb-1.5 flex items-center justify-between">
                  <span className={cn("text-[11px] font-semibold", active ? "text-brand-strong" : "text-muted-foreground")}>
                    {p.phase}
                  </span>
                  {active && (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-brand">
                      <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <div className="text-[13.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{p.title}</div>
                <p className="mt-1.5 text-[11.5px] leading-[1.45] text-ink-2">{p.body}</p>
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
